import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { BiCheckCircle } from "react-icons/bi";
import { FiAlertCircle, FiAlertTriangle } from "react-icons/fi";
import { BsInfo } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import { registerGlobalToast } from "./toastGlobal";

const MAX_TOASTS = 3;
const DEFAULT_DURATION = 5000;

const ToastContext = createContext();

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

const getIcon = (type) => {
  const cls = "w-4 h-4 shrink-0 mt-0.5";
  switch (type) {
    case "success":
      return <BiCheckCircle className={`${cls} text-emerald-600`} />;
    case "error":
      return <FiAlertCircle className={`${cls} text-red-500`} />;
    case "warning":
      return <FiAlertTriangle className={`${cls} text-amber-500`} />;
    default:
      return <BsInfo className={`${cls} text-blue-500`} />;
  }
};

const ToastStack = ({ toasts, removeToast }) => {
  const [hovered, setHovered] = useState(false);

  const visible = toasts.slice(-MAX_TOASTS);
  const reversed = [...visible].reverse();
  const count = reversed.length;

  useEffect(() => {
    if (count === 0) setHovered(false);
  }, [count]);

  if (count === 0) return null;

  const CARD_H = 48;
  const PEEK = 8;
  const GAP = 2.5;

  const collapsedH = CARD_H + (count - 1) * PEEK;
  const expandedH = count * CARD_H + (count - 1) * GAP;

  return (
    <>
      <style>{`
        @keyframes toastShrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
        .toast-card {
          background: white;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 10px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04);
          padding: 12px 14px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          position: relative;
          overflow: hidden;
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          width: 360,
          zIndex: 9999,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Height-animating wrapper */}
        <div
          style={{
            position: "relative",
            height: hovered ? expandedH : collapsedH,
            transition: "height 0.35s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {reversed.map((toast, i) => {
            const isTop = i === 0;
            const cY = i * PEEK;
            const cScale = 1 - i * 0.04;
            const cOp = 1 - i * 0.18;
            const eY = i * (CARD_H + GAP);

            const ty = hovered ? eY : cY;
            const scale = hovered ? 1 : cScale;
            const opacity = hovered ? 1 : cOp;

            const barColor =
              toast.type === "success"
                ? "#10b981"
                : toast.type === "error"
                  ? "#ef4444"
                  : toast.type === "warning"
                    ? "#f59e0b"
                    : "#3b82f6";

            return (
              <div
                key={toast.id}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: count - i,
                  transform: `translateY(${ty}px) scaleX(${scale})`,
                  transformOrigin: "top center",
                  opacity,
                  transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                <div className="toast-card">
                  {/* Icon */}
                  {getIcon(toast.type)}

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--color-text-primary, #111)",
                        lineHeight: 1.4,
                      }}
                    >
                      {typeof toast.message === "object"
                        ? (toast.message.text ?? JSON.stringify(toast.message))
                        : toast.message}
                    </p>
                    {toast.description && (
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: 12,
                          color: "var(--color-text-secondary, #666)",
                          lineHeight: 1.4,
                        }}
                      >
                        {toast.description}
                      </p>
                    )}
                  </div>

                  {/* Dismiss button — always visible on top, visible on all when expanded */}
                  {(isTop || hovered) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeToast(toast.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "2px",
                        cursor: "pointer",
                        color: "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                      aria-label="Dismiss"
                    >
                      <IoClose style={{ width: 15, height: 15 }} />
                    </button>
                  )}

                  {/* Progress bar — only on top card */}
                  {isTop && toast.duration > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: "rgba(0,0,0,0.06)",
                        borderRadius: "0 0 10px 10px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        key={`${toast.id}-bar`}
                        style={{
                          height: "100%",
                          background: barColor,
                          animation: `toastShrink ${toast.duration}ms linear forwards`,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (messageOrObject, type = "info", duration = DEFAULT_DURATION, options = {}) => {
      let message, toastType, finalOptions;

      if (typeof messageOrObject === "object" && messageOrObject !== null) {
        message = messageOrObject.text ?? messageOrObject.message ?? "Notification";
        toastType = messageOrObject.type ?? type;
        finalOptions = { ...options, ...messageOrObject };
      } else {
        message = messageOrObject;
        toastType = type;
        finalOptions = options;
      }

      const id = Date.now() + Math.random();
      const toast = { id, message, type: toastType, duration, ...finalOptions };

      setToasts((prev) => {
        const next = [...prev, toast];
        if (next.length > MAX_TOASTS) {
          const removed = next.splice(0, next.length - MAX_TOASTS);
          removed.forEach((t) => {
            clearTimeout(timersRef.current[t.id]);
            delete timersRef.current[t.id];
          });
        }
        return next;
      });

      if (duration > 0) {
        timersRef.current[id] = setTimeout(() => removeToast(id), duration);
      }

      return id;
    },
    [removeToast],
  );

  const clearAllToasts = useCallback(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    setToasts([]);
  }, []);

  useEffect(() => () => Object.values(timersRef.current).forEach(clearTimeout), []);

  const contextValue = useMemo(
    () => ({ addToast, removeToast, clearAllToasts }),
    [addToast, removeToast, clearAllToasts],
  );

  useEffect(() => {
    registerGlobalToast(addToast);
  }, [addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

export default ToastProvider;
